import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Search, Plus, RefreshCcw, AlertTriangle, AlertCircle, AlertOctagon, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, X, XCircle, LayoutGrid, Layers, FileText, List, LayoutList, Activity, Heart } from 'lucide-react';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { normalizeAccidentRecord, ACCIDENT_STATUS_VALUES, normalizeAccidentStatus } from '../../shared/utils/hrApiRecordNormalizers';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput, CustomTimeInput } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import {
  useHrModalBodyLock,
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  isHrColumnVisible,
  hrTableColSpan,
  HrFormModalTabBar,
  buildHrEmployeeSelectOptions,
  buildHrEmployeePickerPatch,
  resolveHrEmployeeFromLabel,
  resolveHrEmployeeMatricule,
  useHrEmployees,
  sliceHrListItems,
  useHrTableRowSelection,
  createHrHandleToggleColumn,
} from './hrListPageUtils';
import {
  ALL_HR_DATA_REFRESH_EVENTS,
  dispatchHrApiRefresh,
  useHrApiRefreshListener,
} from './hrRefreshEvents';
import { useHrConfig } from './useHrConfig';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { mergeStickySelectClass, QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
registerLocale('ar', ar);

const NS = 'modules.ressources_humaines.pages.accidentsPage';

const HR_TABLE_COLUMNS = [
  { key: 'accident_number', labelKey: 'col_accident_number' },
  { key: 'employee_name', labelKey: 'label_employee' },
  { key: 'accident_date', labelKey: 'col_accident_date' },
  { key: 'location', labelKey: 'label_location' },
  { key: 'type', labelKey: 'label_type' },
  { key: 'severity', labelKey: 'label_severity' },
  { key: 'medical_leave_days', labelKey: 'col_leave' },
  { key: 'status', labelKey: 'label_status' },
  { key: 'actions', required: true },
];

const AccidentsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const { settings } = useSettings();
  const hrConfig = useHrConfig();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const { isDashboardLightMode } = useGlobalTheme();

  const formatAmountDisplay = (val) => {
    if (val == null || val === '') return null;
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('na');
    return formatBrandMoney(n);
  };

  const fmtDate = (val) => (val ? new Date(val).toLocaleDateString(dateLocale) : p('na'));
  const [searchTerm, setSearchTerm] = useState('');
  const [accidents, setAccidents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  
  const { employees: realEmployees } = useHrEmployees();
  
  const [formTab, setFormTab] = useState('accidentInfo');
  
  const [formData, setFormData] = useState({ 
    accident_number: '', employee_id: '', employee_name: '', accident_date: '', accident_time: '',
    report_date: '', location: '', accident_type: '', severity: '', injury_description: '',
    body_part_affected: '', witness_names: '', first_aid_provided: '', status: ACCIDENT_STATUS_VALUES.declared,
    medical_leave_days: '', leave_start: '', leave_end: '', compensation_amount: '',
    insurance_claim_number: '', cnas_notified: false, cnas_notification_date: '',
    investigation_notes: '', corrective_actions: '', notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_accidents');
  const [quickFilter, setQuickFilter] = useState('all');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const loadAccidents = useCallback(async () => {
    try {
      const response = await localApiAdapter.get('/hr/accidents');
      const raw = response?.data ?? response ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setAccidents(sortRecordsNewestFirst(list.map(normalizeAccidentRecord)));
    } catch (error) {
      console.error('Error loading accidents:', error);
      setAccidents([]);
    }
  }, []);

  useEffect(() => { loadAccidents(); }, [loadAccidents]);

  useHrApiRefreshListener(ALL_HR_DATA_REFRESH_EVENTS, loadAccidents);
  useEffect(() => {
    const handleClickOutside = (event) => {      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const generateAccidentNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'accident', 'ACC');
    return generateEntityReference(prefix, accidents, (a) => a.accident_number);
  }, [accidents, settings]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  
  const stats = {
    total: accidents.length,
    graves: accidents.filter(a => a.severity === 'grave' || a.severity === 'très_grave' || a.severity === 'mortel').length,
    avec_arret: accidents.filter(a => parseInt(a.medical_leave_days) > 0).length,
    en_enquete: accidents.filter(a => a.status === ACCIDENT_STATUS_VALUES.investigation).length,
    cette_annee: accidents.filter(a => new Date(a.accident_date).getFullYear() === currentYear).length
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_accidents', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_gravite', name: p('tab_by_severity'), icon: AlertOctagon },
    { id: 'par_type', name: p('tab_by_type'), icon: Layers }
  ]), [t, p]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quick_all'), icon: Layers },
    { id: 'graves', name: p('quick_graves'), icon: AlertOctagon },
    { id: 'avec_arret', name: p('quick_avec_arret'), icon: Activity },
    { id: 'enquete', name: p('quick_enquete'), icon: Search },
    { id: 'ce_mois', name: p('quick_ce_mois'), icon: Calendar }
  ]), [p]);

  const accidentsDistribution = useMemo(() => ({
    all_accidents: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'leger', name: p('dist_leger'), count: accidents.filter(a => a.severity === 'léger').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'modere', name: p('dist_modere'), count: accidents.filter(a => a.severity === 'modéré').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'grave', name: p('dist_grave'), count: accidents.filter(a => a.severity === 'grave').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'tres_grave', name: p('dist_tres_grave'), count: accidents.filter(a => a.severity === 'très_grave').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_gravite: {
      title: p('dist_by_severity'),
      data: [
        { filterKey: 'leger', name: p('dist_leger'), count: accidents.filter(a => a.severity === 'léger').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'modere', name: p('dist_modere'), count: accidents.filter(a => a.severity === 'modéré').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'grave', name: p('dist_grave'), count: accidents.filter(a => a.severity === 'grave').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'tres_grave', name: p('dist_tres_grave'), count: accidents.filter(a => a.severity === 'très_grave').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'mortel', name: p('dist_mortel'), count: accidents.filter(a => a.severity === 'mortel').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { filterKey: 'chute', name: p('dist_chute'), count: accidents.filter(a => a.accident_type === 'chute').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'coupure', name: p('dist_coupure'), count: accidents.filter(a => a.accident_type === 'coupure').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'brulure', name: p('dist_brulure'), count: accidents.filter(a => a.accident_type === 'brûlure').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'collision', name: p('dist_collision'), count: accidents.filter(a => a.accident_type === 'collision').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'electrocution', name: p('dist_electrocution'), count: accidents.filter(a => a.accident_type === 'électrocution').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'intoxication', name: p('dist_intoxication'), count: accidents.filter(a => a.accident_type === 'intoxication').length, color: 'bg-gradient-to-r from-green-500 to-green-600' }
      ]
    }
  }), [p, accidents]);

  const distributionInfo = accidentsDistribution[mainViewMode] || accidentsDistribution.all_accidents;

  const baseFilteredAccidents = accidents.filter(a => {
        if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (searchTerm && !a.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !a.accident_number?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_accidents') {
      if (quickFilter === 'graves' && !['grave', 'très_grave', 'mortel'].includes(a.severity)) return false;
      if (quickFilter === 'avec_arret' && parseInt(a.medical_leave_days) <= 0) return false;
      if (quickFilter === 'enquete' && a.status !== ACCIDENT_STATUS_VALUES.investigation) return false;
      if (quickFilter === 'ce_mois' && new Date(a.accident_date).getMonth() !== currentMonth) return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_accidents':
        case 'par_gravite':
          if (activeDistributionFilter === 'leger' && a.severity !== 'léger') return false;
          if (activeDistributionFilter === 'modere' && a.severity !== 'modéré') return false;
          if (activeDistributionFilter === 'grave' && a.severity !== 'grave') return false;
          if (activeDistributionFilter === 'tres_grave' && a.severity !== 'très_grave') return false;
          if (activeDistributionFilter === 'mortel' && a.severity !== 'mortel') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'chute' && a.accident_type !== 'chute') return false;
          if (activeDistributionFilter === 'coupure' && a.accident_type !== 'coupure') return false;
          if (activeDistributionFilter === 'brulure' && a.accident_type !== 'brûlure') return false;
          if (activeDistributionFilter === 'collision' && a.accident_type !== 'collision') return false;
          if (activeDistributionFilter === 'electrocution' && a.accident_type !== 'électrocution') return false;
          if (activeDistributionFilter === 'intoxication' && a.accident_type !== 'intoxication') return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('employee_name', (r) => r.employee_name || ''),
    textColumnRule('accident_date', (r) => (r.accident_date ? new Date(r.accident_date).toLocaleDateString(dateLocale) : '')),
    textColumnRule('severity', (r) => r.severity || ''),
    textColumnRule('medical_leave_days', (r) => String(r.medical_leave_days ?? '')),
  ], [dateLocale]);

  const filteredAccidents = useMemo(
    () => applyColumnFilters(baseFilteredAccidents, colFilters, columnFilterRules),
    [baseFilteredAccidents, colFilters, columnFilterRules],
  );

  const pagedFilteredAccidents = useMemo(
    () => sliceHrListItems(filteredAccidents, hrConfig.itemsPerPage),
    [filteredAccidents, hrConfig.itemsPerPage],
  );


  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(pagedFilteredAccidents, selectedRecords, setSelectedRecords);
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = [
      { field: 'employee_name', label: p('col_employee_name') },
      { field: 'accident_date', label: p('col_accident_date') },
      { field: 'report_date', label: p('col_report_date') },
      { field: 'location', label: p('label_accident_location') },
      { field: 'accident_type', label: p('col_accident_type') },
      { field: 'severity', label: p('label_severity') }
    ];
    
    const missingFields = requiredFields.filter(
      ({ field }) => !formData[field] || formData[field].trim() === ''
    );
    
    if (missingFields.length > 0) {
      const missingLabels = missingFields.map(({ label }) => label).join(', ');
      alert(p('alert_required_fields', { fields: missingLabels }));
      return;
    }
    
    try {
      const accidentNumber = (formData.accident_number || '').trim() || generateAccidentNumber();
      const submitData = { 
        ...formData,
        accident_number: accidentNumber,
        employee_id: formData.employee_id ? String(formData.employee_id) : null,
        medical_leave_days: parseInt(formData.medical_leave_days) || 0,
        compensation_amount: parseFloat(formData.compensation_amount) || 0,
        cnas_notified: formData.cnas_notified ? 1 : 0
      };
      if (editingRecord) {
        await localApiAdapter.put(`/hr/accidents/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الحادث' }));
      } else {
        await localApiAdapter.post('/hr/accidents', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الحادث' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('na') }));
      return;
    } finally {
      resetForm();
      loadAccidents();
      dispatchHrApiRefresh('/hr/accidents');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      accident_number: record.accident_number || '', employee_id: record.employee_id?.toString() || '',
      employee_name: record.employee_name || '', accident_date: record.accident_date || '',
      accident_time: record.accident_time || '', report_date: record.report_date || '',
      location: record.location || '', accident_type: record.accident_type || '',
      severity: record.severity || '', injury_description: record.injury_description || '',
      body_part_affected: record.body_part_affected || '', witness_names: record.witness_names || '',
      first_aid_provided: record.first_aid_provided || '', status: normalizeAccidentStatus(record.status),
      medical_leave_days: record.medical_leave_days?.toString() || '', leave_start: record.leave_start || '',
      leave_end: record.leave_end || '', compensation_amount: record.compensation_amount?.toString() || '',
      insurance_claim_number: record.insurance_claim_number || '', cnas_notified: record.cnas_notified === 1,
      cnas_notification_date: record.cnas_notification_date || '', investigation_notes: record.investigation_notes || '',
      corrective_actions: record.corrective_actions || '', notes: record.notes || ''
    });
    setFormTab('accidentInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/hr/accidents/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الحادث' }));
        loadAccidents();
        dispatchHrApiRefresh('/hr/accidents');
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف الحادث' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/hr/accidents/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'حادث' }));
        setSelectedRecords([]);
        loadAccidents();
        dispatchHrApiRefresh('/hr/accidents');
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف الحوادث المحددة' }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      accident_number: '', employee_id: '', employee_name: '', accident_date: '', accident_time: '',
      report_date: '', location: '', accident_type: '', severity: '', injury_description: '',
      body_part_affected: '', witness_names: '', first_aid_provided: '', status: ACCIDENT_STATUS_VALUES.declared,
      medical_leave_days: '', leave_start: '', leave_end: '', compensation_amount: '',
      insurance_claim_number: '', cnas_notified: false, cnas_notification_date: '',
      investigation_notes: '', corrective_actions: '', notes: ''
    });
    setEditingRecord(null);
    setFormTab('accidentInfo');
    setShowModal(false);
  };

  const openNewAccidentModal = () => {
    setEditingRecord(null);
    setFormData({
      accident_number: generateAccidentNumber(),
      employee_id: '',
      employee_name: '',
      accident_date: '',
      accident_time: '',
      report_date: '',
      location: '',
      accident_type: '',
      severity: '',
      injury_description: '',
      body_part_affected: '',
      witness_names: '',
      first_aid_provided: '',
      status: ACCIDENT_STATUS_VALUES.declared,
      medical_leave_days: '',
      leave_start: '',
      leave_end: '',
      compensation_amount: '',
      insurance_claim_number: '',
      cnas_notified: false,
      cnas_notification_date: '',
      investigation_notes: '',
      corrective_actions: '',
      notes: ''
    });
    setFormTab('accidentInfo');
    setShowModal(true);
  };

  const handleExport = () => {
    const headers = [
      { key: 'accident_number', label: p('export_col_number') },
      { key: 'employee_name', label: p('label_employee') },
      { key: 'accident_date', label: p('label_date') },
      { key: 'location', label: p('export_col_location') },
      { key: 'accident_type', label: p('label_type') },
      { key: 'severity', label: p('label_severity') },
      { key: 'status', label: p('label_status') },
      { key: 'medical_leave_days', label: p('export_col_leave_days') },
      { key: 'compensation_amount', label: p('label_compensation') }
    ];
    const fileName = `accidents_export_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToCSV(filteredAccidents, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('alert_export_error'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `<div class="report-content"><table><thead><tr><th>${p('label_employee')}</th><th>${p('label_date')}</th><th>${p('label_type')}</th><th>${p('label_severity')}</th><th>${p('col_leave')}</th><th>${p('label_status')}</th></tr></thead><tbody>${filteredAccidents.map(item => `<tr><td>${item.employee_name || p('na')}</td><td>${fmtDate(item.accident_date)}</td><td>${item.accident_type || p('na')}</td><td>${item.severity || p('na')}</td><td>${item.medical_leave_days || 0} ${p('days_suffix')}</td><td>${item.status || p('na')}</td></tr>`).join('')}</tbody></table></div>`;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `<div class="report-content"><table><thead><tr><th>${p('export_col_number')}</th><th>${p('label_employee')}</th><th>${p('label_date')}</th><th>${p('export_col_time')}</th><th>${p('export_col_location')}</th><th>${p('label_type')}</th><th>${p('label_severity')}</th><th>${p('label_status')}</th><th>${p('export_col_leave_days')}</th><th>${p('export_col_gosi')}</th><th>${p('label_compensation')}</th></tr></thead><tbody>${filteredAccidents.map(item => `<tr><td>${item.accident_number || ''}</td><td>${item.employee_name || ''}</td><td>${item.accident_date ? new Date(item.accident_date).toLocaleDateString(dateLocale) : ''}</td><td>${item.accident_time || ''}</td><td>${item.location || ''}</td><td>${item.accident_type || ''}</td><td>${item.severity || ''}</td><td>${item.status || ''}</td><td>${item.medical_leave_days || 0}</td><td>${item.cnas_notified ? p('yes') : p('no')}</td><td>${item.compensation_amount ? formatBrandMoney(item.compensation_amount) : ''}</td></tr>`).join('')}</tbody></table></div>`;
      document.body.appendChild(tempDiv);
      await generateReportExcel('accidents', p('report_excel_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'accident_number', label: p('export_col_number') },
      { key: 'employee_name', label: p('label_employee') },
      { key: 'accident_date', label: p('label_date') },
      { key: 'location', label: p('export_col_location') },
      { key: 'accident_type', label: p('label_type') },
      { key: 'severity', label: p('label_severity') },
      { key: 'status', label: p('label_status') }
    ];

    const schema = {
      requiredFields: ['accident_number', 'employee_name', 'accident_date', 'location', 'accident_type', 'severity'],
      validators: {
        severity: (value) => ['léger', 'modéré', 'grave', 'très_grave', 'mortel'].includes(value),
        status: (value) => !value || Object.values(ACCIDENT_STATUS_VALUES).includes(normalizeAccidentStatus(value))
          || ['déclaré', 'enquête', 'clos', 'contentieux'].includes(value)
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/hr/accidents', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadAccidents();
          alert(p('alert_import_success', { total: result.totalCount, imported: successCount }) + (result.invalidCount > 0 ? '\n' + p('alert_import_errors', { count: result.invalidCount, details: result.errors.slice(0, 5).join('\n') }) : ''));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };
  const statusOptions = useMemo(() => ([
    { value: ACCIDENT_STATUS_VALUES.declared, label: p('status_declare'), icon: FileText, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: ACCIDENT_STATUS_VALUES.investigation, label: p('status_enquete'), icon: Search, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: ACCIDENT_STATUS_VALUES.closed, label: p('status_clos'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: ACCIDENT_STATUS_VALUES.dispute, label: p('status_contentieux'), icon: AlertCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ]), [p]);

  const getAccidentStatusLabel = useCallback((status) => {
    const labels = {
      [ACCIDENT_STATUS_VALUES.declared]: p('status_declare'),
      [ACCIDENT_STATUS_VALUES.investigation]: p('status_enquete'),
      [ACCIDENT_STATUS_VALUES.closed]: p('status_clos'),
      [ACCIDENT_STATUS_VALUES.dispute]: p('status_contentieux'),
    };
    return labels[normalizeAccidentStatus(status)] || p('empty_dash');
  }, [p]);

  const statusFilterOptions = useMemo(() => ([
    { value: 'all', label: p('status_all'), dot: 'bg-white/50' },
    { value: ACCIDENT_STATUS_VALUES.declared, label: p('status_declare'), dot: 'bg-blue-400' },
    { value: ACCIDENT_STATUS_VALUES.investigation, label: p('status_enquete'), dot: 'bg-orange-400' },
    { value: ACCIDENT_STATUS_VALUES.closed, label: p('status_clos'), dot: 'bg-green-400' },
    { value: ACCIDENT_STATUS_VALUES.dispute, label: p('status_contentieux'), dot: 'bg-red-400' }
  ]), [p]);

  const accidentTypeFormOptions = useMemo(() => ([
    { value: 'chute', label: p('type_chute') },
    { value: 'coupure', label: p('type_coupure') },
    { value: 'brûlure', label: p('type_brulure') },
    { value: 'collision', label: p('type_collision') },
    { value: 'électrocution', label: p('type_electrocution') },
    { value: 'intoxication', label: p('type_intoxication') },
    { value: 'autre', label: p('type_autre') }
  ]), [p]);

  const severityFormOptions = useMemo(() => ([
    { value: 'léger', label: p('severity_leger') },
    { value: 'modéré', label: p('severity_modere') },
    { value: 'grave', label: p('severity_grave') },
    { value: 'très_grave', label: p('severity_tres_grave') },
    { value: 'mortel', label: p('severity_mortel') }
  ]), [p]);

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(
      HR_TABLE_COLUMNS.map((col) => (
        col.key === 'actions' ? { ...col, label: t('common.actions') } : col
      )),
      p,
      columnVisibility,
    ),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/hr/accidents/${recordId}`, { status: newStatus });
      await loadAccidents();
      dispatchHrApiRefresh('/hr/accidents');
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getSeverityBadge = useCallback((severity) => {
    const badges = {
      léger: { label: p('severity_leger'), style: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      modéré: { label: p('severity_modere'), style: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: AlertCircle },
      grave: { label: p('severity_grave'), style: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertTriangle },
      très_grave: { label: p('severity_tres_grave'), style: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertOctagon },
      mortel: { label: p('severity_mortel'), style: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: XCircle }
    };
    const config = badges[severity] || badges.léger;
    const Icon = config.icon;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${config.style}`}><Icon size={14} />{config.label}</span>;
  }, [p]);

  const getTypeBadge = useCallback((type) => {
    const badges = {
      chute: { label: p('type_chute'), style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      coupure: { label: p('type_coupure'), style: 'bg-red-500/20 text-red-400 border-red-500/30' },
      brûlure: { label: p('type_brulure'), style: 'bg-red-500/20 text-red-400 border-red-500/30' },
      collision: { label: p('type_collision'), style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      électrocution: { label: p('type_electrocution'), style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      intoxication: { label: p('type_intoxication'), style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      autre: { label: p('type_autre'), style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
    };
    const config = badges[type] || badges.autre;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${config.style}`}>{config.label}</span>;
  }, [p]);

  const accidentFormTabs = useMemo(() => ([
    { id: 'accidentInfo', label: p('section_accident'), icon: AlertTriangle, iconClassName: 'text-blue-400' },
    { id: 'injuryCompensation', label: p('section_compensation'), icon: Calendar, iconClassName: 'text-green-400' },
    { id: 'investigationActions', label: p('section_investigation'), icon: Shield, iconClassName: 'text-amber-400' },
  ]), [p]);
  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-blue-400" />
{p('title')}
            <button type="button" onClick={loadAccidents} className="refresh-button" title={p('refresh_title')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400'
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={p('advanced_options_title')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
            onClick={openNewAccidentModal}
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_item')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: Shield, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_graves'), value: stats.graves, icon: AlertOctagon, color: 'from-red-500 to-red-600' },
          { title: p('stat_avec_arret'), value: stats.avec_arret, icon: Activity, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_en_enquete'), value: stats.en_enquete, icon: Search, color: 'from-amber-500 to-amber-600' },
          { title: p('stat_ce_annee'), value: stats.cette_annee, icon: Calendar, color: 'from-purple-500 to-purple-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group/card">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover/card:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover/card:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_accidents' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-yellow-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`} title={p('filter_click_title', { name: item.name })}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }}></div>
                  </div>
<p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('pct_of_accidents', { pct: (() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden shadow-xl shadow-black/10">

        

        

        {viewMode === 'table' && (
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} /></div></th>
                  {isColVisible('accident_number') && (
                  <TableColumnHeaderFilter
                    columnKey="accident_number"
                    label={p('col_accident_number')}
                    value={colFilters.accident_number ?? ''}
                    onChange={(v) => setColFilter('accident_number', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('employee_name') && (
                  <TableColumnHeaderFilter
                    columnKey="employee_name"
                    label={p('label_employee')}
                    value={colFilters.employee_name ?? ''}
                    onChange={(v) => setColFilter('employee_name', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('accident_date') && (
                  <TableColumnHeaderFilter
                    columnKey="accident_date"
                    label={p('col_accident_date')}
                    value={colFilters.accident_date ?? ''}
                    onChange={(v) => setColFilter('accident_date', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('location') && <th className="px-6 py-4 text-left text-white font-semibold">{p('label_location')}</th>}
                  {isColVisible('type') && <th className="px-6 py-4 text-left text-white font-semibold">{p('label_type')}</th>}
                  {isColVisible('severity') && (
                  <TableColumnHeaderFilter
                    columnKey="severity"
                    label={p('label_severity')}
                    value={colFilters.severity ?? ''}
                    onChange={(v) => setColFilter('severity', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('medical_leave_days') && (
                  <TableColumnHeaderFilter
                    columnKey="medical_leave_days"
                    label={p('col_leave')}
                    value={colFilters.medical_leave_days ?? ''}
                    onChange={(v) => setColFilter('medical_leave_days', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('status') && <th className="px-6 py-4 text-center text-white font-semibold">{p('label_status')}</th>}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {pagedFilteredAccidents.length > 0 ? (
                  pagedFilteredAccidents.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} /></div></td>
                      {isColVisible('accident_number') && (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{record.accident_number || p('na')}</span>
                      </td>
                      )}
                      {isColVisible('employee_name') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.employee_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('matricule_prefix')} {resolveHrEmployeeMatricule(realEmployees, record) || p('na')} | {p('card_number_prefix')} {record.accident_number} | {p('label_cnas')}: {record.cnas_notified ? p('cnas_notified') : p('cnas_not_notified')}</span>
                        </div>
                      </td>
                      )}
                      {isColVisible('accident_date') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Calendar size={14} className="text-white/50" />
                            {record.accident_date ? new Date(record.accident_date).toLocaleDateString(dateLocale) : '-'}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">{p('row_time_prefix')} {record.accident_time || p('na')} | {p('row_declared_prefix')} {record.report_date ? new Date(record.report_date).toLocaleDateString(dateLocale) : p('na')}</span>
                        </div>
                      </td>
                      )}
                      {isColVisible('location') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.location || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('row_body_part_prefix')} {record.body_part_affected || p('na')}</span>
                        </div>
                      </td>
                      )}
                      {isColVisible('type') && (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getTypeBadge(record.accident_type)}</div></td>
                      )}
                      {isColVisible('severity') && (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getSeverityBadge(record.severity)}</div></td>
                      )}
                      {isColVisible('medical_leave_days') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.medical_leave_days || 0} {p('days_suffix')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.leave_start ? `${new Date(record.leave_start).toLocaleDateString(dateLocale)} - ${record.leave_end ? new Date(record.leave_end).toLocaleDateString(dateLocale) : ''}` : p('no_medical_leave')}</span>
                        </div>
                      </td>
                      )}
                      {isColVisible('status') && (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} /></div></td>
                      )}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <AlertTriangle size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {pagedFilteredAccidents.length > 0 ? (
              pagedFilteredAccidents.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <AlertTriangle size={20} className="text-orange-400 shrink-0" />
                          <span className="truncate">{record.employee_name}</span>
                        </h3>
                        <div className="flex flex-wrap gap-2 items-center">
                          {getTypeBadge(record.accident_type)}
                          {getSeverityBadge(record.severity)}
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_accident_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_number_prefix')}</span> {record.accident_number}</div>
                            <div><span className="font-medium">{p('label_matricule')}:</span> {resolveHrEmployeeMatricule(realEmployees, record) || p('na')}</div>
                            <div><span className="font-medium">{p('label_date')}:</span> {record.accident_date ? new Date(record.accident_date).toLocaleDateString(dateLocale) : '-'}</div>
                            <div><span className="font-medium">{p('label_accident_time')}:</span> {record.accident_time || p('na')}</div>
                            <div><span className="font-medium">{p('label_declaration_date')}</span> {record.report_date ? new Date(record.report_date).toLocaleDateString(dateLocale) : '-'}</div>
                            <div><span className="font-medium">{p('label_location')}:</span> {record.location || '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_injury')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_description')}:</span> {record.injury_description || '-'}</div>
                            <div><span className="font-medium">{p('label_body_part')}:</span> {record.body_part_affected || '-'}</div>
                            <div><span className="font-medium">{p('label_first_aid')}:</span> {record.first_aid_provided || p('no')}</div>
                            <div><span className="font-medium">{p('label_witnesses')}:</span> {record.witness_names || p('none')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_medical_leave')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_duration')}:</span> {record.medical_leave_days || 0} {p('days_suffix')}</div>
                            <div><span className="font-medium">{p('label_start')}:</span> {record.leave_start ? new Date(record.leave_start).toLocaleDateString(dateLocale) : '-'}</div>
                            <div><span className="font-medium">{p('label_end')}:</span> {record.leave_end ? new Date(record.leave_end).toLocaleDateString(dateLocale) : '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_compensation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_amount')}:</span> {formatAmountDisplay(record.compensation_amount || 0)}</div>
                            <div><span className="font-medium">{p('label_insurance_claim')}:</span> {record.insurance_claim_number || '-'}</div>
                            <div><span className="font-medium">{p('label_cnas')}:</span> {record.cnas_notified ? `✓ ${record.cnas_notification_date ? new Date(record.cnas_notification_date).toLocaleDateString(dateLocale) : p('cnas_notified')}` : p('cnas_not_notified')}</div>
                          </div>
                        </div>
                      </div>
                      {(record.investigation_notes || record.corrective_actions || record.notes) && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            {record.investigation_notes && (
                              <div className="p-3 bg-white/5 rounded-lg">
                                <h5 className="font-medium text-white/80 text-xs mb-1">{p('section_investigation')}</h5>
                                <p className="text-white/60 text-xs">{record.investigation_notes}</p>
                              </div>
                            )}
                            {record.corrective_actions && (
                              <div className="p-3 bg-white/5 rounded-lg">
                                <h5 className="font-medium text-white/80 text-xs mb-1">{p('section_corrective')}</h5>
                                <p className="text-white/60 text-xs">{record.corrective_actions}</p>
                              </div>
                            )}
                            {record.notes && (
                              <div className="p-3 bg-white/5 rounded-lg">
                                <h5 className="font-medium text-white/80 text-xs mb-1">{p('label_notes')}</h5>
                                <p className="text-white/60 text-xs">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <AlertTriangle size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Modal — layout aligned with EmployeesPage view modal */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => { setShowViewModal(false); setViewingRecord(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button type="button" onClick={() => { setShowViewModal(false); setViewingRecord(null); }} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div >
                {/* Accident & déclaration — same card + 4-col grid as Employees “Informations Personnelles” */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Shield size={18} className="text-blue-400" />
{p('section_accident_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_accident_number')}</p>
                      <p className="text-white font-medium">{viewingRecord.accident_number || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_employee')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.employee_name || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_matricule')}</p>
                      <p className="text-white font-medium">{resolveHrEmployeeMatricule(realEmployees, viewingRecord) || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_accident_date')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.accident_date ? new Date(viewingRecord.accident_date).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_accident_time')}</p>
                      <p className="text-white font-medium">{viewingRecord.accident_time || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_report_date')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.report_date ? new Date(viewingRecord.report_date).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_location')}</p>
                      <p className="text-white font-medium">{viewingRecord.location || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_type')}</p>
                      <div className="flex flex-wrap">{getTypeBadge(viewingRecord.accident_type)}</div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_severity')}</p>
                      <div className="flex flex-wrap">{getSeverityBadge(viewingRecord.severity)}</div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_status')}</p>
                      <div className="flex flex-wrap">
                        <QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Blessure — icon header + grid like Employees “Informations Professionnelles” */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Heart size={18} className="text-red-400" />
                    {p('section_injury')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-4">
                      <p className="text-white/60 text-sm mb-1">{p('label_injury_description')}</p>
                      <p className="text-white font-medium">{viewingRecord.injury_description || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_body_part')}</p>
                      <p className="text-white font-medium">{viewingRecord.body_part_affected || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_first_aid')}</p>
                      <p className="text-white font-medium">{viewingRecord.first_aid_provided || p('none')}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-white/60 text-sm mb-1">{p('label_witnesses')}</p>
                      <p className="text-white font-medium">{viewingRecord.witness_names || p('none')}</p>
                    </div>
                  </div>
                </div>

                {/* Bottom: two columns like Contact & Salariales */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_leave_compensation')}</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_medical_leave_days')}</p>
                        <p className="text-white font-medium">{viewingRecord.medical_leave_days || 0} {p('days_suffix')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_period')}</p>
                        <p className="text-white font-medium">
                          {viewingRecord.leave_start
                            ? `${new Date(viewingRecord.leave_start).toLocaleDateString(dateLocale)} — ${viewingRecord.leave_end ? new Date(viewingRecord.leave_end).toLocaleDateString(dateLocale) : p('empty_dash')}`
                            : p('na')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_compensation')}</p>
                        <p className="text-white font-medium text-lg">
                          {formatAmountDisplay(viewingRecord.compensation_amount || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_insurance_claim')}</p>
                        <p className="text-white font-medium">{viewingRecord.insurance_claim_number || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_cnas_notified')}</p>
                        <p className="text-white font-medium">
                          {viewingRecord.cnas_notified
                            ? `${p('yes')}${viewingRecord.cnas_notification_date ? ` — ${new Date(viewingRecord.cnas_notification_date).toLocaleDateString(dateLocale)}` : ''}`
                            : p('no')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_investigation_actions')}</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_investigation_notes')}</p>
                        <p className="text-white font-medium">{viewingRecord.investigation_notes || p('none_notes')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_corrective_actions')}</p>
                        <p className="text-white font-medium">{viewingRecord.corrective_actions || p('no_corrective_action')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_notes')}</p>
                        <p className="text-white font-medium">{viewingRecord.notes || p('none_notes')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit') : p('modal_create')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="accident-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />{editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            
            <HrFormModalTabBar
              tabs={accidentFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="accident-form" onSubmit={handleSubmit} >
                {formTab === 'accidentInfo' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Row 1: N° Accident, Nom Employé, Date Accident */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_accident_number')} <span className="qawaem-required-mark">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.accident_number}
                              onChange={(e) => setFormData({ ...formData, accident_number: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('ph_accident_number')}
                            />
                            {!editingRecord && (
                              <p className="text-white/50 text-xs mt-1">{p('hint_auto_number')}</p>
                            )}
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_employee_name')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.employee_name}
                              onChange={(value) => {
                                const selectedEmployee = resolveHrEmployeeFromLabel(realEmployees, value);
                                if (selectedEmployee) {
                                  setFormData({ ...formData, ...buildHrEmployeePickerPatch(selectedEmployee) });
                                } else {
                                  setFormData({ ...formData, employee_name: value, employee_id: '' });
                                }
                              }}
                              options={buildHrEmployeeSelectOptions(realEmployees)}
                              placeholder={p('placeholder_select_employee')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_employee_select')}</p>
                          </div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_accident_date')} <span className="qawaem-required-mark">*</span></label><CustomDatePicker value={formData.accident_date} onChange={(value) => setFormData({ ...formData, accident_date: value })} placeholder={p('placeholder_date')} /></div>
                        </div>
                        
                        {/* Row 2: Heure, Date Déclaration, Lieu */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_accident_time')}</label><CustomTimeInput value={formData.accident_time} onChange={(value) => setFormData({ ...formData, accident_time: value })} placeholder="HH:MM" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_report_date')} <span className="qawaem-required-mark">*</span></label><CustomDatePicker value={formData.report_date} onChange={(value) => setFormData({ ...formData, report_date: value })} placeholder={p('placeholder_date')} /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_accident_location')} <span className="qawaem-required-mark">*</span></label><input type="text" required value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_location')} /></div>
                        </div>
                        
                        {/* Row 3: Type Accident, Gravité, Statut */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_accident_type')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.accident_type}
                              onChange={(value) => setFormData({ ...formData, accident_type: value })}
                              options={accidentTypeFormOptions}
                              placeholder={p('placeholder_select_type')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_select_type')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_severity')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.severity}
                              onChange={(value) => setFormData({ ...formData, severity: value })}
                              options={severityFormOptions}
                              placeholder={p('placeholder_select_severity')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_select_severity')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={statusOptions.map(({ value, label }) => ({ value, label }))}
                              placeholder={p('placeholder_select_status')}
                            />
                          </div>
                        </div>
                    </div>
                  </>
                )}
                {formTab === 'injuryCompensation' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_injury_description')}</label><textarea value={formData.injury_description} onChange={(e) => setFormData({ ...formData, injury_description: e.target.value })} rows="3" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_injury')} /></div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_body_part')}</label><input type="text" value={formData.body_part_affected} onChange={(e) => setFormData({ ...formData, body_part_affected: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_body_part')} /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_first_aid')}</label><input type="text" value={formData.first_aid_provided} onChange={(e) => setFormData({ ...formData, first_aid_provided: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_first_aid')} /></div>
                        </div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_witnesses')}</label><input type="text" value={formData.witness_names} onChange={(e) => setFormData({ ...formData, witness_names: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_witnesses')} /></div>
                    </div>
                  </>
                )}
                {formTab === 'injuryCompensation' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_medical_leave_days')}</label><CustomNumberInput value={formData.medical_leave_days} onChange={(value) => setFormData({ ...formData, medical_leave_days: value })} placeholder="0" /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_leave_start')}</label><CustomDatePicker value={formData.leave_start} onChange={(value) => setFormData({ ...formData, leave_start: value })} placeholder={p('placeholder_date')} /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_leave_end')}</label><CustomDatePicker value={formData.leave_end} onChange={(value) => setFormData({ ...formData, leave_end: value })} placeholder={p('placeholder_date')} /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_compensation')} (${getBrandCurrencySuffix()})</label><CustomNumberInput value={formData.compensation_amount} onChange={(value) => setFormData({ ...formData, compensation_amount: value })} placeholder="0.00" step="0.01" /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_insurance_claim')}</label><input type="text" value={formData.insurance_claim_number} onChange={(e) => setFormData({ ...formData, insurance_claim_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="ASS-2026-001" /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_cnas_notification_date')}</label><CustomDatePicker value={formData.cnas_notification_date} onChange={(value) => setFormData({ ...formData, cnas_notification_date: value })} placeholder={p('placeholder_date')} /></div>
                        <div className="md:col-span-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={formData.cnas_notified} onChange={(e) => setFormData({ ...formData, cnas_notified: e.target.checked })} className="w-5 h-5 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-500" />
                            <span className="text-white font-medium">{p('label_cnas_notified')}</span>
                          </label>
                        </div>
                    </div>
                  </>
                )}
                {formTab === 'investigationActions' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_investigation_notes')}</label><textarea value={formData.investigation_notes} onChange={(e) => setFormData({ ...formData, investigation_notes: e.target.value })} rows="3" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_investigation')} /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_corrective_actions')}</label><textarea value={formData.corrective_actions} onChange={(e) => setFormData({ ...formData, corrective_actions: e.target.value })} rows="3" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_corrective')} /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="3" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_notes')} /></div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default AccidentsPage;

